import NetxQueryClient, {
	type SuccessResponse,
} from '@netx/core-next/lib/queryClient';
export type {QueryError} from '@netx/core-next/lib/queryClient';
import {type IAsset} from '../schema/asset';
import type {SearchCriteria} from '@netx/core-search-rules/types';
import {isBrandPortal, getCurrentPortalProp} from '@netx/core-cms-next/utils';
import {
	DEFAULT_SEARCH_MATCH_CRITERIA,
	DEFAULT_SEARCH_SORT_FIELD,
	DEFAULT_SEARCH_SORT_ORDER,
} from '@netx/core-search-rules/constants';
import {SearchFacetFilterMode} from '@netx/core-search-facets/constants';
import type {SearchFacetSelection} from '@netx/core-search-facets/types';
import {type ISearchGalleryAssets} from '@netx/core-asset-gallery/schema/searchGalleryAsset/searchGalleryAssetSchema';
import {convertAssetDataToGalleryAssetData} from '@netx/core-asset-gallery/utils/convertAssetDataToGalleryAssetData';

export type SearchAssetObjectsPaging = {
	startIndex: number;
	stopIndex: number;
};

export const FACETED_SEARCH_DEFAULTS = {
	sortField: DEFAULT_SEARCH_SORT_FIELD,
	sortOrder: DEFAULT_SEARCH_SORT_ORDER,
	matchCriteria: DEFAULT_SEARCH_MATCH_CRITERIA,
	elementTypes: [],
	elementSubType1s: [],
	elementSubType2s: [],
	elementValue1s: [],
	elementValue2s: [],
	elementValue3s: [''],
	startIndex: 0,
	stopIndex: 40,
};

/**
 * `searchAssetObjects` request params
 * (what is used to send to server)
 */
export interface SearchAssetObjectsRequestOptions
	extends Partial<SearchAssetObjectsPaging>, SearchCriteria {}

type RequiredSearchAssetObjectsRequestOptions =
	Required<SearchAssetObjectsRequestOptions>;

/**
 * `searchAssetObjects` request
 * (what gets sent to the server)
 */
export type SearchAssetObjectsRequest = [
	RequiredSearchAssetObjectsRequestOptions['sortField'],
	RequiredSearchAssetObjectsRequestOptions['sortOrder'],
	RequiredSearchAssetObjectsRequestOptions['matchCriteria'],
	RequiredSearchAssetObjectsRequestOptions['elementTypes'],
	RequiredSearchAssetObjectsRequestOptions['elementSubType1s'],
	RequiredSearchAssetObjectsRequestOptions['elementSubType2s'],
	RequiredSearchAssetObjectsRequestOptions['elementValue1s'],
	RequiredSearchAssetObjectsRequestOptions['elementValue2s'],
	RequiredSearchAssetObjectsRequestOptions['elementValue3s'],
	string,
	number,
	number,
	RequiredSearchAssetObjectsRequestOptions['startIndex'],
	number, // size
];

/**
 * `searchAssetObjects` response
 * (what comes back from the server)
 */
export type SearchAssetObjectsResponse = SuccessResponse<
	Omit<SearchAssetObjectsResult, 'results' | 'startIndex'> & {
		results: IAsset[];
	}
>;

/**
 * `searchAssetObjects` result
 * (what is utilized from the server response)
 */
export interface SearchAssetObjectsResult extends ISearchGalleryAssets {
	startIndex: number;
}

export const searchAssetObjectsEndpoint = 'searchAssetObjects';

/**
 * Search assets (faceted)
 * @param params Fetch params
 */
export const searchAssetObjects = async (
	params: SearchAssetObjectsRequestOptions,
	signal?: AbortSignal,
): Promise<SearchAssetObjectsResult> => {
	const client = NetxQueryClient();

	const {
		sortField,
		sortOrder,
		matchCriteria,
		elementTypes,
		elementSubType1s,
		elementSubType2s,
		elementValue1s,
		elementValue2s,
		elementValue3s,
		startIndex,
		stopIndex,
		...facet
	} = {
		...FACETED_SEARCH_DEFAULTS,
		...params,
	};

	Object.assign(
		facet,
		elementTypes,
		elementSubType1s,
		elementSubType2s,
		elementValue1s,
		elementValue2s,
		elementValue3s,
	);

	const requestParams: SearchAssetObjectsRequest = [
		sortField,
		sortOrder,
		matchCriteria,
		elementTypes,
		elementSubType1s,
		elementSubType2s,
		elementValue1s,
		elementValue2s,
		elementValue3s,
		'',
		0,
		0,
		startIndex + 1,
		stopIndex - startIndex,
	];

	const response = await client.post<SearchAssetObjectsResponse>(
		searchAssetObjectsEndpoint,
		requestParams,
		{signal},
	);

	return {
		...response.data.result,
		startIndex,
		results: response.data.result.results.map((asset) => ({
			...convertAssetDataToGalleryAssetData(asset),
			values: {},
		})),
	};
};
export default searchAssetObjects;
